import { defineStore } from 'pinia';
import {
  CheckPromoCodeGroupRequestDTO,
  CheckPromoCodeRequestDTO,
  SearchAvailabilityRequestDTO,
} from 'src/services/api/data-contracts';
import { useNewBookingStore } from 'src/stores/newBookingStore';
import { useRoomTypeAvailabilityStore } from 'src/stores/roomTypeAvailabilityStore';

import { Services } from 'src/services/Services';
import { Api } from 'src/services/api/Api';
import { useServiceStore } from 'src/stores/serviceStore';
import { CreateBookingProcessStep } from 'src/types/createBooking';
import { IRoom, RoomTypeAvailability } from 'src/types/common';
import useRateHelperUtils from 'src/composables/rateHelperUtils';
import { i18n } from 'boot/i18n';
import { usePropertyStore } from './propertyStore';
import { usePropertyGroupStore } from './propertyGroupStore';
import useAnalyticUtils from 'src/composables/analyticUtils';

const rateHelperUtils = useRateHelperUtils();
const t = i18n.global.t;

const apiService = Services.getService(Api);

export const useSearchAvailabilityStore = defineStore(
  'searchAvailabilityStore',
  {
    state: () => ({
      isSearching: false as boolean,
      searchHasBeenPerformed: false as boolean,
      isPromoCodeValid: true as boolean,
      promoCodeMessage: '' as string,
      isHopGiftVoucherCodeValid: true as boolean,
      hopGiftVoucherCodeMessage: '' as string,
      restrictedBookingWarning: '' as string,
    }),
    getters: {
      availableRoomTypesForActiveRoomSelection(): Array<RoomTypeAvailability> {
        const rtaas = useRoomTypeAvailabilityStore();

        const newBookingStore = useNewBookingStore();
        const activeReservationItem = newBookingStore.activeReservationItem;
        const activeRoom = newBookingStore.activeRoom;

        const roomId = activeReservationItem?.roomId ?? activeRoom?.roomId;
        const resX = newBookingStore.getReservationItem(roomId);

        const roomData: IRoom = activeReservationItem ?? activeRoom;

        const availableRoomTypes =
          rtaas.getAvailableRoomTypesForActiveRoomSelection(
            roomData,
            resX?.RoomTypeId,
            newBookingStore.roomTypeId,
          );

        return availableRoomTypes;
      },
      totalAvailableRooms(): number {
        const rtaas = useRoomTypeAvailabilityStore();
        return rtaas.totalAvailableRooms;
      },

      totalLowestRate(): number {
        const newBookingStore = useNewBookingStore();
        const roomTypes = useRoomTypeAvailabilityStore().RoomTypes;
        const total = rateHelperUtils.calculateTotalRoomRate(
          newBookingStore.rooms,
          roomTypes,
          false,
        );
        return total;
      },
      totalOriginalLowestRate(): number {
        const newBookingStore = useNewBookingStore();
        const roomTypes = useRoomTypeAvailabilityStore().RoomTypes;
        const total = rateHelperUtils.calculateTotalRoomRate(
          newBookingStore.rooms,
          roomTypes,
          true,
        );
        return total;
      },
      hasResults(): boolean {
        return useRoomTypeAvailabilityStore().hasResults;
      },
      hasRoomTypesForActiveRoomSelection(): boolean {
        return this.availableRoomTypesForActiveRoomSelection.length > 0;
      },
      showResults(): boolean {
        return this.hasResults && this.isSearching == false;
      },
      showPromoCodeWarning(): boolean {
        return (
          !this.isSearching &&
          !this.isPromoCodeValid &&
          this.promoCodeMessage != '' &&
          this.promoCodeMessage != null
        );
      },
      showNoRoomsAvailableForOccupancyWarning(): boolean {
        return (
          !this.isSearching &&
          this.hasResults &&
          !this.hasRoomTypesForActiveRoomSelection
        );
      },
      showNoRoomsAvailableWarning(): boolean {
        return (
          (!this.isSearching &&
            !this.hasResults &&
            this.promoCodeMessage == '' &&
            this.noRoomsAvailableMessage != '') ||
          !this.hasRoomTypesForActiveRoomSelection
        );
      },
      noRoomsAvailableMessage(): string {
        const nb = useNewBookingStore();
        let noAvailabilityMessage = nb.noAvailabilityMessage;
        if (!this.hasRoomTypesForActiveRoomSelection) {
          noAvailabilityMessage = t(
            'roomsAndRates.notEnoughRoomsBasedOnOccupancy',
          );
        }
        return noAvailabilityMessage;
      },
      showVoucherCodeMessage(): boolean {
        return this.hopGiftVoucherCodeMessage && this.isHopGiftVoucherCodeValid;
      },
    },
    actions: {
      /*
      TODO 21/05/2025
      The below actions are maintained for ease of use for now.
      */
      decreaseAvailabilityByOne(roomTypeId: number) {
        const rtaas = useRoomTypeAvailabilityStore();
        const a = this.getAvailability(roomTypeId);
        rtaas.availableRoomsPerRoomTypeId[roomTypeId] = a - 1;
      },
      increaseAvailabilityByOne(roomTypeId: number) {
        const rtaas = useRoomTypeAvailabilityStore();
        const a = this.getAvailability(roomTypeId);
        rtaas.availableRoomsPerRoomTypeId[roomTypeId] = a + 1;
      },
      updateAvailability(roomTypeId: number, newAvailability: number) {
        const rtaas = useRoomTypeAvailabilityStore();
        rtaas.availableRoomsPerRoomTypeId[roomTypeId] = newAvailability;
      },
      getAvailability(roomTypeId: number): number | null {
        const rtaas = useRoomTypeAvailabilityStore();
        const obs = rtaas.availableRoomsPerRoomTypeId[roomTypeId];
        return obs ? obs : null;
      },
      showAvailabilityBadge(roomTypeId: number): boolean {
        const rtaas = useRoomTypeAvailabilityStore();
        return rtaas.showAvailabilityBadge(roomTypeId);
      },

      async checkPromoCode(promoCode: string) {
        const propertyStore = usePropertyStore();

        const query = {
          propertyId: propertyStore.propertyId,
          promoCode: promoCode,
        } as CheckPromoCodeRequestDTO;

        return apiService.availabilityCheckPromoCode(query);
      },

      checkPromoCodeGroup(promoCode: string) {
        const propertyGroupStore = usePropertyGroupStore();

        const query = {
          propertyGroupId: propertyGroupStore.propertyGroupId,
          promoCode: promoCode,
        } as CheckPromoCodeGroupRequestDTO;

        return apiService.availabilityCheckPromoCodeGroup(query);
      },

      async search() {
        this.isSearching = true;
        const propertyStore = usePropertyStore();
        const newBookingStore = useNewBookingStore();
        const roomTypeAvailabilityStore = useRoomTypeAvailabilityStore();

        newBookingStore.setRooms(newBookingStore.searchControlRooms);

        newBookingStore.resetActiveRoom();
        newBookingStore.resetActiveReservationItem();
        newBookingStore.resetBasket();

        const serviceStore = useServiceStore();
        serviceStore.clearCache();

        const dto: SearchAvailabilityRequestDTO = {
          PropertyId: newBookingStore.propertyId,
          LanguageCode: newBookingStore.languageCode,
          PromoCode: newBookingStore.promoCode,
          RatePlanCode: newBookingStore.ratePlanCode,
          DateFrom: newBookingStore.fromDate,
          DateTo: newBookingStore.toDate,
          LocationId: newBookingStore.location?.LocationId,
          Rooms: newBookingStore.rooms.map((r) => {
            const g = {
              NumberOfAdults: r.NumberOfAdults,
              NumberOfChildren: r.NumberOfChildren,
              NumberOfInfants: r.NumberOfInfants,
            };
            return g;
          }),
        };

        const searchDataResponse = await apiService.availabilitySearch(dto);

        roomTypeAvailabilityStore.setRoomTypes(
          searchDataResponse.data.RoomTypes.map(
            (d) => new RoomTypeAvailability(d),
          ),
        );

        this.promoCodeMessage = searchDataResponse.data.PromoCodeMessage;
        this.isPromoCodeValid = searchDataResponse.data.IsPromoCodeValid;
        newBookingStore.isPromoCodeValid = this.isPromoCodeValid;

        this.hopGiftVoucherCodeMessage =
          searchDataResponse.data.HopGiftVoucherCodeMessage;
        this.isHopGiftVoucherCodeValid =
          searchDataResponse.data.IsHopGiftVoucherCodeValid;

        newBookingStore.visitorLevy = searchDataResponse.data.VisitorLevy;

        roomTypeAvailabilityStore.initializeAvailableRoomsPerRoomType(
          searchDataResponse.data.NumberOfRoomsPerRoomTypeId,
        );

        const bestRate = this.totalLowestRate;

        const analyticUtils = useAnalyticUtils();
        analyticUtils.trackSearchAvailabilitySearch(
          propertyStore.propertyCode,
          newBookingStore.fromDate,
          newBookingStore.toDate,
          newBookingStore.numberOfNights,
          propertyStore.currency,
          bestRate,
          newBookingStore.ratePlanCode,
          newBookingStore.promoCode,
          newBookingStore.rooms,
        );

        newBookingStore.processStep =
          CreateBookingProcessStep.RoomTypeSelection;
        newBookingStore.next();

        this.searchHasBeenPerformed = true;
        this.isSearching = false;
      },
    },
  },
);
